import type { ContentOptimizationPhase } from "@schemas/shared/contentOptimizationPhaseSchema";

import type { Status } from "@braze/beacon-core";

import type { PushType, SelectableMessageType } from "@lib/campaignMessageTypeConstants";
import type { ComposerSection } from "@lib/canvas";
import type { MessagingService } from "@lib/messageComposition";

import type { OptimizationConversionConfig, OptimizationEventType } from "@canvas/Steps/ContentOptimizationStep";

import type { PushImagePlatform } from "./pushImageVariantRules";

export interface ExcludedLinkPrefixesValidationResult {
  duplicateRowIndices: Set<number>;
  overlongRowIndices: Set<number>;
  exceedsCountLimit: boolean;
}

/**
 * Step-document `component_store` (`visible_components` / snake_case).
 * Distinct from composer `ComponentStoreData` (`components` / camelCase).
 */
export type ComponentStoreExport = {
  visible_components: Array<{ name: string; values: string[] }>;
  deactivated_values: Record<string, string[]>;
  variant_metadata?: Record<string, Record<string, string>[]>;
};

export type ComponentStoreData = {
  /**
   * Map of component name → variant text values that are active for the step.
   * Keys are component names (e.g. "Subject", "Title").
   * Values are the ordered list of variant strings for that component.
   */
  components: Record<string, string[]>;
  /**
   * Map of component name → variant text values that have been explicitly deactivated.
   * Keys are the same component names used in `components`.
   * Values are the variant strings that have been turned off for bandit arm selection.
   */
  deactivatedValues?: Record<string, string[]>;
  /**
   * Post-launch draft-only map of component name -> values that exist in draft but not live.
   */
  newValues?: Record<string, string[]>;
  /**
   * Map of component name -> per-variant metadata hashes, aligned with `components` value order.
   */
  variantMetadata?: Record<string, VariantMetadata[]>;
};

/** Per-variant metadata for structured CO components (e.g. Image). */
export type VariantMetadata = {
  alt_text?: string;
  file_type?: string;
  aspect_ratio?: string;
  file_name?: string;
};

/**
 * A kept component variant and its original index in the values array.
 * Used to zip variant metadata when filtering/picking active entries.
 */
export type ComponentValueEntry = {
  value: string;
  index: number;
};

export interface SuggestionsData {
  suggestions: string[];
}

export type ComponentSuggestionsData = Record<string, SuggestionsData>;

export const emailComponentNames = [
  "Subject",
  "Preheader",
  "Sender Name",
  "Body Header",
  "Body Content",
  "Primary CTA",
  "Image",
] as const;
export type EmailComponentName = (typeof emailComponentNames)[number];

export const pushComponentNames = ["Title", "Message", "Image"] as const;
export type PushComponentName = (typeof pushComponentNames)[number];

export const smsComponentNames = ["Hook", "Body", "CTA", "Image"] as const;
export type SmsComponentName = (typeof smsComponentNames)[number];

export type ContentOptimizationComponentName = EmailComponentName | PushComponentName | SmsComponentName;
export const contentOptimizationComponentNames = Array.from(
  new Set<ContentOptimizationComponentName>([...emailComponentNames, ...pushComponentNames, ...smsComponentNames])
);

// typing for the locations that text errors can be attributed
export const emailErrorTextKeys = ["body", "sending_info"] as const;
export type EmailErrorTextKey = (typeof emailErrorTextKeys)[number];
export const pushErrorTextKeys = ["generic"] as const;
export type PushErrorTextKey = (typeof pushErrorTextKeys)[number];
export const smsErrorTextKeys = ["generic"] as const;
export type SmsErrorTextKey = (typeof smsErrorTextKeys)[number];
export type ContentOptimizationErrorTextKey = EmailErrorTextKey | PushErrorTextKey | SmsErrorTextKey;
export const contentOptimizationErrorTextKeys = [
  ...emailErrorTextKeys,
  ...pushErrorTextKeys,
  ...smsErrorTextKeys,
] as ContentOptimizationErrorTextKey[];

export const contentOptimizationChannels = ["email", "push", "sms"] as const;
export type ContentOptimizationChannel = (typeof contentOptimizationChannels)[number];

export type ContentOptimizationDuplicateChannelSelection = ContentOptimizationChannel | PushType;

export type RcsImageEligibilityRcsMessage = {
  type?: string | null;
  rich_card_type?: string | null;
  media_url?: string | null;
};

export type RcsImageEligibilityInput = {
  protocol?: string | null;
  selected_messaging_service?: MessagingService | null;
  rcs_message?: RcsImageEligibilityRcsMessage | null;
};

/** SMS/RCS message shape accepted by CO composer SMS update guards (canvas store or composer view model). */
export type ContentOptimizationSmsMessage = RcsImageEligibilityInput & {
  message_type: "sms";
};

/**
 * Gate an SMS/RCS message write (e.g. confirm before dropping Image).
 * Call `applyUpdate` in this turn to commit; skipping it means the write did not
 * land, so the composer may reset RHF from the current message — form fields can
 * already disagree with it when this runs.
 */
export type OnSmsMessageChange = (
  currentMessage: ContentOptimizationSmsMessage,
  nextMessage: ContentOptimizationSmsMessage,
  applyUpdate: () => void
) => void;

export type UseContentOptimizationSmsMessageGuardParams = {
  enabled: boolean;
  componentStoreData: ComponentStoreData;
  removeComponentByName: (componentName: string) => void;
};

export type UseContentOptimizationSmsMessageGuardResult = {
  onSmsMessageChange?: OnSmsMessageChange;
  isDeleteImageComponentModalOpen: boolean;
  onCancelDeleteImageComponent: () => void;
  onConfirmDeleteImageComponent: () => void;
};

export { contentOptimizationPhases } from "@schemas/shared/contentOptimizationPhaseSchema";
export type { ContentOptimizationPhase };

export interface ContentOptimizationEmailLink {
  active: boolean;
  is_tracked: boolean;
  url: string;
  lid?: string;
  name?: string;
}

export interface ChannelOptimizationEventConfig {
  allowed: readonly OptimizationEventType[];
  default: OptimizationEventType;
}

export type ComponentInvalidStatus = Extract<Status, "danger" | "warning">;

export interface ContentOptimizationMessageErrors {
  missingActiveComponentNames: string[];
  keyToUnknownComponentNames: Partial<Record<ContentOptimizationErrorTextKey, string[]>>;
  keyToInactiveComponentNames: Partial<Record<ContentOptimizationErrorTextKey, string[]>>;
}

export interface ContentOptimizationErrors extends ContentOptimizationMessageErrors {
  /**
   * The status to represent the overall component configuration at the top-level, which is null if valid.
   */
  overallComponentInvalidStatus: ComponentInvalidStatus | null;
  /**
   * The mapping from any component which is invalid to its status.
   */
  componentNameToInvalidStatus: Record<string, ComponentInvalidStatus>;
  /***
   * Whether the message is expected to need updates to be valid, so it is considered incomplete.
   */
  isMessageIncomplete: boolean;

  noComponentsConfigured: boolean;
  componentNamesWithRecursiveTags: string[];
  componentNamesWithTooFewValues: string[];
  componentNamesWithTooManyValues: string[];
  componentNamesWithDuplicateValues: string[];
  componentNamesWithWhitespaceOnlyValues: string[];
  opensOptimizationBodyOnly: boolean;
  clickOptimizationMissingLink: boolean;
  clickOptimizationAllLinksExcluded: boolean;
  excludedLinkPrefixesDuplicate?: boolean;
  excludedLinkPrefixesLimitExceeded?: boolean;
  excludedLinkPrefixTooLong?: boolean;
  pushImageMissingPlacement?: boolean;
  pushImageUnsupportedPlacement?: boolean;
  pushImageInvalidConfiguration?: boolean;
}

export type PushImageMessageField =
  | "action-button-text"
  | "action-button-url"
  | "android-notification-image"
  | "android-summary-text"
  | "ios-notification-image"
  | "message"
  | "push-icon-image"
  | "title"
  | "web-notification-image";

export interface PushImageMessageFieldValue {
  field: PushImageMessageField;
  value?: string | null;
}

export interface PushImageMessagePlacement {
  platform: PushImagePlatform;
  supportedFields: PushImageMessageFieldValue[];
  unsupportedFields: PushImageMessageFieldValue[];
}

export interface PushImageMessagePlacementValidationResult {
  hasInvalidTarget: boolean;
  hasMissingPlacement: boolean;
  unsupportedFields: PushImageMessageField[];
}

export interface PushImageVariantValidationResult {
  hasMissingPlacement: boolean;
  hasUnsupportedPlacement: boolean;
  hasInvalidTargets: boolean;
  hasIncompatibleVariants: boolean;
}

export interface ContentOptimizationContextStruct {
  componentStoreData: ComponentStoreData;
  selectedComponentOverrides: Record<string, string>;
  setSelectedComponentOverrides: (overrides: Record<string, string>) => void;
  componentSuggestionsData?: ComponentSuggestionsData;
  setComponentSuggestionsData?: (data: ComponentSuggestionsData) => void;
  generationInstructions?: string;
  setGenerationInstructions?: (instructions: string) => void;
  isContentOptimization: boolean;
  validationErrors?: ContentOptimizationErrors;
  channel?: ContentOptimizationChannel | null;
  setChannel?: (channel: ContentOptimizationChannel | null) => void;
  optimizationEventConfig?: ChannelOptimizationEventConfig | null;
  activeComponentTab?: string | null;
  setActiveComponentTab?: (tab: string | null) => void;
  /**
   * True if this step has not been launched yet. Note this will be false for
   * post-launch drafts of pre-existing steps as well.
   */
  isDraft: boolean;
  isComponentStoreReadOnly?: boolean;
  composerSection?: ComposerSection;
  setComposerSection?: (section: ComposerSection) => void;
  addComponentFromComponentTag?: (componentName: string) => void;
  deleteComponentModalState?: {
    isOpen: boolean;
    componentName: string | null;
  };
  openDeleteComponentModal?: (componentName: string) => void;
  closeDeleteComponentModal?: () => void;
  confirmDeleteComponent?: () => void;
  isComponentLimitModalOpen?: boolean;
  setIsComponentLimitModalOpen?: (isOpen: boolean) => void;
  optimizationConversionConfig?: OptimizationConversionConfig;
  setOptimizationConversionConfig?: (value: OptimizationConversionConfig) => void;
  allowedChannels?: SelectableMessageType[];
  emailLinks?: ContentOptimizationEmailLink[];
  emailSenderDisplayName?: string | null;
  emailSubject?: string | null;
  emailPreheader?: string | null;
  pushTitle?: string | null;
  pushMessage?: string | null;
  pushImagePlatforms?: PushImagePlatform[];
  rcsImageEligible?: boolean;
  variantActivationWarningDismissed?: boolean;
  dismissVariantActivationWarning?: () => void;
  optimizationPhase?: ContentOptimizationPhase | null;
  displayOptimizationPhase?: ContentOptimizationPhase | null;
  onSmsMessageChange?: OnSmsMessageChange;
}

export interface ContentOptimizationLaunchLimit {
  limit: number | null;
  activeCount: number;
}

export interface GetIsComponentStoreReadOnlyOptions {
  connectedContentOptimizationStepCount: number;
  isCanvasActive: boolean;
  launchLimit?: ContentOptimizationLaunchLimit;
  isStepConnected?: boolean;
  isStepDraft?: boolean;
}

export function isContentOptimizationComponentName(
  componentName: string
): componentName is ContentOptimizationComponentName {
  return componentName ? (contentOptimizationComponentNames as readonly string[]).includes(componentName) : false;
}

export function isContentOptimizationEmailErrorTextKey(key: string): key is EmailErrorTextKey {
  return key ? (emailErrorTextKeys as readonly string[]).includes(key) : false;
}

export interface ComponentInvalidStatusIconProps {
  status: ComponentInvalidStatus;
}
